/**
 * StatsPanel Component
 * --------------------
 * Studio Console Design: Recessed panel with amber/cyan data displays
 * Now includes timing statistics from the metronome evaluation.
 */

import { motion, AnimatePresence } from "framer-motion";
import {
  Flame,
  Target,
  Zap,
  Trophy,
  Star,
  Music,
  Calendar,
  Eye,
  Award,
  Clock,
} from "lucide-react";
import type { GameState, SessionStats, Achievement } from "@/hooks/useGameState";
import type { TimingResult } from "@/hooks/useMetronome";

interface StatsPanelProps {
  gameState: GameState;
  session: SessionStats;
  newAchievements: Achievement[];
  showLevelUp: boolean;
  allAchievements: Achievement[];
  timingResults?: TimingResult[];
}

const iconMap: Record<string, React.ReactNode> = {
  music: <Music size={13} />,
  flame: <Flame size={13} />,
  star: <Star size={13} />,
  trophy: <Trophy size={13} />,
  target: <Target size={13} />,
  calendar: <Calendar size={13} />,
  eye: <Eye size={13} />,
};

export default function StatsPanel({
  gameState,
  session,
  newAchievements,
  showLevelUp,
  allAchievements,
  timingResults = [],
}: StatsPanelProps) {
  const xpProgress =
    gameState.xpToNextLevel > 0
      ? Math.min(100, (gameState.currentXp / gameState.xpToNextLevel) * 100)
      : 0;

  // Timing stats
  const timingCount = timingResults.length;
  const onTimeCount = timingResults.filter(t => t.onTime).length;
  const avgDeviation = timingCount > 0
    ? Math.round(timingResults.reduce((a, t) => a + t.absoluteMs, 0) / timingCount)
    : 0;
  const perfectCount = timingResults.filter(t => t.rating === "perfect").length;
  const timingAccuracy = timingCount > 0 ? Math.round((onTimeCount / timingCount) * 100) : 0;

  return (
    <div className="flex flex-col gap-2.5 h-full overflow-y-auto pr-0.5 pb-4">
      {/* Level Up Animation */}
      <AnimatePresence>
        {showLevelUp && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            className="panel p-3 text-center glow-amber"
          >
            <div className="flex items-center justify-center gap-2">
              <Zap size={16} style={{ color: "oklch(0.82 0.17 75)" }} />
              <span className="text-sm font-bold font-mono" style={{ color: "oklch(0.82 0.17 75)" }}>
                LEVEL UP!
              </span>
            </div>
            <div className="text-2xl font-bold font-mono mt-1" style={{ color: "oklch(0.90 0.01 270)" }}>
              Level {gameState.level}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Achievement Toast */}
      <AnimatePresence>
        {newAchievements.map((ach) => (
          <motion.div
            key={ach.id}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 40 }}
            className="panel p-2.5 glow-amber"
          >
            <div className="flex items-center gap-2">
              <Award size={15} style={{ color: "oklch(0.82 0.17 75)" }} />
              <div>
                <div className="text-xs font-bold" style={{ color: "oklch(0.82 0.17 75)" }}>
                  {ach.title}
                </div>
                <div className="text-[10px]" style={{ color: "oklch(0.55 0.01 270)" }}>
                  {ach.description}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Level & XP */}
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center font-mono font-bold text-sm"
              style={{
                background: "linear-gradient(135deg, oklch(0.82 0.17 75), oklch(0.68 0.17 75))",
                color: "oklch(0.16 0.015 270)",
                boxShadow: "0 2px 8px oklch(0.82 0.17 75 / 25%)",
              }}
            >
              {gameState.level}
            </div>
            <div>
              <div className="text-xs font-semibold" style={{ color: "oklch(0.75 0.01 270)" }}>
                Level {gameState.level}
              </div>
              <div className="text-[10px] font-mono" style={{ color: "oklch(0.45 0.01 270)" }}>
                {gameState.totalXp} XP total
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-mono font-medium" style={{ color: "oklch(0.82 0.17 75)" }}>
              {gameState.currentXp}/{gameState.xpToNextLevel}
            </div>
          </div>
        </div>
        {/* Custom XP progress bar */}
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: "oklch(0.22 0.012 270)", boxShadow: "inset 0 1px 2px oklch(0 0 0 / 30%)" }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{
              background: "linear-gradient(90deg, oklch(0.82 0.17 75), oklch(0.75 0.17 60))",
              boxShadow: "0 0 6px oklch(0.82 0.17 75 / 30%)",
            }}
            animate={{ width: `${xpProgress}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Session Stats */}
      <div className="panel p-3">
        <SectionTitle>Session</SectionTitle>
        <div className="grid grid-cols-2 gap-1.5">
          <StatBox
            label="Accuracy"
            value={`${session.accuracy}%`}
            color={session.accuracy >= 80 ? "oklch(0.82 0.17 75)" : session.accuracy >= 50 ? "oklch(0.82 0.14 200)" : "oklch(0.65 0.24 25)"}
          />
          <StatBox label="Streak" value={String(session.streak)} color="oklch(0.82 0.14 200)" icon={<Flame size={10} />} />
          <StatBox label="Correct" value={String(session.correctNotes)} color="oklch(0.82 0.17 75)" />
          <StatBox label="Wrong" value={String(session.wrongNotes)} color="oklch(0.65 0.24 25)" />
          <StatBox label="Best Streak" value={String(session.bestStreak)} color="oklch(0.82 0.14 200)" />
          <StatBox label="XP Earned" value={`+${session.xpEarned}`} color="oklch(0.82 0.17 75)" />
        </div>
      </div>

      {/* Timing Stats */}
      {timingCount > 0 && (
        <div className="panel p-3">
          <SectionTitle>
            <span className="flex items-center gap-1">
              <Clock size={10} />
              Timing
            </span>
          </SectionTitle>
          <div className="grid grid-cols-2 gap-1.5">
            <StatBox
              label="On Time"
              value={`${timingAccuracy}%`}
              color={timingAccuracy >= 80 ? "oklch(0.70 0.18 150)" : timingAccuracy >= 50 ? "oklch(0.82 0.14 200)" : "oklch(0.65 0.24 25)"}
            />
            <StatBox
              label="Avg Dev"
              value={`${avgDeviation}ms`}
              color={avgDeviation <= 50 ? "oklch(0.70 0.18 150)" : "oklch(0.75 0.18 30)"}
            />
            <StatBox
              label="Perfect"
              value={String(perfectCount)}
              color="oklch(0.70 0.18 150)"
            />
            <StatBox
              label="On/Total"
              value={`${onTimeCount}/${timingCount}`}
              color="oklch(0.82 0.14 200)"
            />
          </div>
        </div>
      )}

      {/* Global Stats */}
      <div className="panel p-3">
        <SectionTitle>All Time</SectionTitle>
        <div className="space-y-1">
          <StatRow label="Total Notes" value={String(gameState.totalNotesPlayed)} />
          <StatRow label="Total Correct" value={String(gameState.totalCorrect)} />
          <StatRow label="Best Streak" value={String(gameState.bestStreak)} />
          <StatRow label="Exercises" value={String(gameState.exercisesCompleted)} />
          <StatRow
            label="Daily Streak"
            value={`${gameState.dailyStreak} day${gameState.dailyStreak !== 1 ? "s" : ""}`}
          />
        </div>
      </div>

      {/* Achievements */}
      <div className="panel p-3">
        <SectionTitle>
          Achievements ({gameState.achievements.length}/{allAchievements.length})
        </SectionTitle>
        <div className="grid grid-cols-3 gap-1">
          {allAchievements.map((ach) => {
            const unlocked = gameState.achievements.some((a) => a.id === ach.id);
            return (
              <div
                key={ach.id}
                className="flex flex-col items-center gap-0.5 p-1.5 rounded text-center transition-opacity"
                style={{
                  background: unlocked ? "oklch(0.22 0.012 270)" : "oklch(0.16 0.015 270)",
                  opacity: unlocked ? 1 : 0.3,
                }}
                title={`${ach.title}: ${ach.description}`}
              >
                <div style={{ color: unlocked ? "oklch(0.82 0.17 75)" : "oklch(0.35 0.01 270)" }}>
                  {iconMap[ach.icon] || <Star size={13} />}
                </div>
                <span
                  className="text-[7px] leading-tight font-medium"
                  style={{ color: unlocked ? "oklch(0.65 0.01 270)" : "oklch(0.35 0.01 270)" }}
                >
                  {ach.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-[9px] uppercase tracking-widest font-semibold mb-2"
      style={{ color: "oklch(0.45 0.01 270)" }}
    >
      {children}
    </div>
  );
}

function StatBox({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: string;
  color: string;
  icon?: React.ReactNode;
}) {
  return (
    <div
      className="p-2 text-center rounded"
      style={{
        background: "oklch(0.14 0.015 270)",
        border: "1px solid oklch(0.22 0.012 270)",
        boxShadow: "inset 0 1px 3px oklch(0 0 0 / 20%)",
      }}
    >
      <div className="text-[8px] uppercase tracking-wider mb-0.5" style={{ color: "oklch(0.42 0.01 270)" }}>
        {label}
      </div>
      <div className="flex items-center justify-center gap-0.5">
        {icon && <span style={{ color }}>{icon}</span>}
        <span className="text-sm font-mono font-bold" style={{ color }}>
          {value}
        </span>
      </div>
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[10px]" style={{ color: "oklch(0.48 0.01 270)" }}>
        {label}
      </span>
      <span className="text-[11px] font-mono font-medium" style={{ color: "oklch(0.72 0.01 270)" }}>
        {value}
      </span>
    </div>
  );
}
